import React, { useState } from 'react';
import {
  Layers, Sparkles, ChevronDown, ChevronRight,
  Download, Copy, Search, Star
} from 'lucide-react';
import { PlateStack } from '../components/PlateStack';

export const ComponentLibrary: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('buttons');
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(new Set());

  const toggleExpanded = (componentId: string) => {
    const newExpanded = new Set(expandedComponents);
    if (newExpanded.has(componentId)) {
      newExpanded.delete(componentId);
    } else {
      newExpanded.add(componentId);
    }
    setExpandedComponents(newExpanded);
  };

  const components = {
    buttons: {
      title: 'Buttons & Actions',
      description: 'Interactive elements for user actions',
      items: [
        {
          id: 'consulting-slate-blue-button',
          name: 'Primary Button',
          description: 'Main call-to-action buttons',
          preview: (
            <div className="space-y-3">
              <button className="bg-consulting-slate-blue-600 text-white px-6 py-3 font-semibold hover:bg-consulting-slate-blue-700 transition-colors">
                Primary Action
              </button>
              <button className="bg-consulting-slate-blue-600 text-white px-6 py-3 font-semibold hover:bg-consulting-slate-blue-700 transition-colors flex items-center gap-2">
                <Download className="h-4 w-4" />
                Download
              </button>
            </div>
          ),
          code: `<button className="bg-consulting-slate-blue-600 text-white px-6 py-3 font-semibold hover:bg-consulting-slate-blue-700 transition-colors">
  Primary Action
</button>`
        },
        {
          id: 'secondary-button',
          name: 'Secondary Button',
          description: 'Secondary actions and alternatives',
          preview: (
            <div className="space-y-3">
              <button className="bg-[var(--hover-tint)] text-charcoal-700 px-6 py-3 font-semibold hover:bg-[var(--hover-tint)] transition-colors">
                Secondary Action
              </button>
              <button className="border-2 border-consulting-slate-blue-600 text-consulting-slate-blue-600 px-6 py-3 font-semibold hover:bg-consulting-slate-blue-50 transition-colors">
                Outline Button
              </button>
            </div>
          ),
          code: `<button className="bg-[var(--hover-tint)] text-charcoal-700 px-6 py-3 font-semibold hover:bg-[var(--hover-tint)] transition-colors">
  Secondary Action
</button>`
        }
      ]
    },
    cards: {
      title: 'Cards & Containers',
      description: 'Content containers and information cards',
      items: [
        {
          id: 'basic-card',
          name: 'Basic Card',
          description: 'Standard content card with header, body, and footer',
          preview: (
            <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6 max-w-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-consulting-slate-blue-100">
                  <Layers className="h-5 w-5 text-consulting-slate-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-charcoal-900">Card Title</h3>
                  <p className="text-sm text-charcoal-600">Subtitle or description</p>
                </div>
              </div>
              <p className="text-charcoal-700 mb-4">
                Card content goes here. This is where you'd put the main information or description.
              </p>
              <div className="flex justify-between items-center">
                <span className="text-xs text-charcoal-500">Category</span>
                <button className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 text-sm font-medium">
                  Learn More →
                </button>
              </div>
            </div>
          ),
          code: `<div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
  <div className="flex items-center gap-3 mb-4">
    <div className="p-2 bg-consulting-slate-blue-100">
      <Layers className="h-5 w-5 text-consulting-slate-blue-600" />
    </div>
    <div>
      <h3 className="font-bold text-charcoal-900">Card Title</h3>
      <p className="text-sm text-charcoal-600">Subtitle</p>
    </div>
  </div>
  <p className="text-charcoal-700 mb-4">Card content...</p>
  <div className="flex justify-between items-center">
    <span className="text-xs text-charcoal-500">Category</span>
    <button className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 text-sm font-medium">
      Learn More →
    </button>
  </div>
</div>`
        },
        {
          id: 'featured-card',
          name: 'Featured Card',
          description: 'Highlighted card with special styling',
          preview: (
            <div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 shadow-lg border-2 border-[var(--mustard)] p-6 max-w-sm relative">
              <div className="absolute top-4 right-4">
                <Sparkles className="h-5 w-5 text-[var(--mustard)]" />
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-[var(--mustard-dim)]">
                  <Star className="h-5 w-5 text-[var(--mustard)]" />
                </div>
                <div>
                  <h3 className="font-bold text-charcoal-900">Featured Content</h3>
                  <p className="text-sm text-charcoal-600">Special highlight</p>
                </div>
              </div>
              <p className="text-charcoal-700 mb-4">
                This card has special styling to draw attention to important content.
              </p>
              <button className="w-full bg-[var(--mustard)] text-white py-2 font-semibold hover:bg-[var(--mustard)] transition-colors">
                Take Action
              </button>
            </div>
          ),
          code: `<div className="bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 shadow-lg border-2 border-[var(--mustard)] p-6 relative">
  <div className="absolute top-4 right-4">
    <Sparkles className="h-5 w-5 text-[var(--mustard)]" />
  </div>
  <!-- Card content -->
</div>`
        }
      ]
    },
    navigation: {
      title: 'Navigation & Menus',
      description: 'Navigation components and menu systems',
      items: [
        {
          id: 'tab-navigation',
          name: 'Tab Navigation',
          description: 'Horizontal tab navigation',
          preview: (
            <div className="space-y-4">
              <div className="flex gap-2">
                <button className="bg-[var(--mustard)] text-white px-4 py-2 font-semibold">
                  Active Tab
                </button>
                <button className="bg-[var(--hover-tint)] text-charcoal-700 px-4 py-2 font-semibold hover:bg-[var(--hover-tint)]">
                  Inactive Tab
                </button>
                <button className="bg-[var(--hover-tint)] text-charcoal-700 px-4 py-2 font-semibold hover:bg-[var(--hover-tint)]">
                  Another Tab
                </button>
              </div>
            </div>
          ),
          code: `<div className="flex gap-2">
  <button className="bg-[var(--mustard)] text-white px-4 py-2 font-semibold">
    Active Tab
  </button>
  <button className="bg-[var(--hover-tint)] text-charcoal-700 px-4 py-2 font-semibold hover:bg-[var(--hover-tint)]">
    Inactive Tab
  </button>
</div>`
        },
        {
          id: 'breadcrumb',
          name: 'Breadcrumb Navigation',
          description: 'Hierarchical navigation breadcrumbs',
          preview: (
            <nav className="flex items-center space-x-2 text-sm">
              <a href="#" className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700">Home</a>
              <ChevronRight className="h-4 w-4 text-charcoal-400" />
              <a href="#" className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700">Library</a>
              <ChevronRight className="h-4 w-4 text-charcoal-400" />
              <span className="text-charcoal-600">Current Page</span>
            </nav>
          ),
          code: `<nav className="flex items-center space-x-2 text-sm">
  <a href="#" className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700">Home</a>
  <ChevronRight className="h-4 w-4 text-charcoal-400" />
  <a href="#" className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700">Library</a>
  <ChevronRight className="h-4 w-4 text-charcoal-400" />
  <span className="text-charcoal-600">Current Page</span>
</nav>`
        }
      ]
    },
    forms: {
      title: 'Forms & Inputs',
      description: 'Form elements and input components',
      items: [
        {
          id: 'text-input',
          name: 'Text Input',
          description: 'Standard text input fields',
          preview: (
            <div className="space-y-4 max-w-sm">
              <div>
                <label className="block text-sm font-medium text-charcoal-900 mb-2">
                  Label
                </label>
                <input 
                  type="text" 
                  placeholder="Enter text here..."
                  className="w-full px-4 py-3 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-600 focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal-900 mb-2">
                  Search Input
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-charcoal-400" />
                  <input 
                    type="text" 
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-3 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-600 focus:outline-none transition-colors"
                  />
                </div>
              </div>
            </div>
          ),
          code: `<div>
  <label className="block text-sm font-medium text-charcoal-900 mb-2">
    Label
  </label>
  <input 
    type="text" 
    placeholder="Enter text here..."
    className="w-full px-4 py-3 border-2 border-[var(--rule)] focus:border-consulting-slate-blue-600 focus:outline-none transition-colors"
  />
</div>`
        }
      ]
    },
    visuals: {
      title: 'Visual Patterns',
      description: 'Reusable visual abstractions and spot art',
      items: [
        {
          id: 'plate-stack-default',
          name: 'Plate Stack (Default)',
          description: 'Four plates, 25° tilt, mono fill, spacious offsets',
          preview: (
            <div className="flex items-center justify-center">
              <PlateStack width={360} density="spacious" />
            </div>
          ),
          code: `<PlateStack width={360} density="spacious" />`
        },
        {
          id: 'plate-stack-outline',
          name: 'Plate Stack (Outline)',
          description: 'Outline-only variant for diagrams',
          preview: (
            <div className="flex items-center justify-center">
              <PlateStack width={360} variant="outline" monoFill="none" strokeColor="#111827" />
            </div>
          ),
          code: `<PlateStack width={360} variant="outline" monoFill="none" strokeColor="#111827" />`
        },
        {
          id: 'plate-stack-lead-accent',
          name: 'Plate Stack (Lead Accent)',
          description: 'Top plate accent border only',
          preview: (
            <div className="flex items-center justify-center">
              <PlateStack width={360} variant="lead-accent" accentColor="#E3B505" />
            </div>
          ),
          code: `<PlateStack width={360} variant="lead-accent" accentColor="#E3B505" />`
        },
        {
          id: 'plate-stack-cutaway-rail',
          name: 'Plate Stack (Cutaway + Rail)',
          description: 'Brand micro-signature: clipped corner + label rail',
          preview: (
            <div className="flex items-center justify-center">
              <PlateStack width={360} variant="label-rail" cutaway />
            </div>
          ),
          code: `<PlateStack width={360} variant="label-rail" cutaway />`
        },
        {
          id: 'plate-stack-svg-parity',
          name: 'Plate Stack (SVG Parity)',
          description: 'Matches provided outline sample: 4 plates, -25° tilt, currentColor stroke',
          preview: (
            <div className="flex items-center justify-center text-charcoal-800">
              <PlateStack width={360} variant="outline" monoFill="none" strokeColor="currentColor" tiltDeg={-25} count={4} />
            </div>
          ),
          code: `<PlateStack width={360} variant="outline" monoFill="none" strokeColor="currentColor" tiltDeg={-25} count={4} />`
        }
      ]
    }
  };

  const currentCategory = components[selectedCategory as keyof typeof components];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Layers className="h-10 w-10 text-[var(--mustard)]" />
          <h1 className="text-4xl font-bold text-charcoal-900">Component Library</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
          Reusable UI components built with the Peak Priorities design system. Copy, customize, and implement.
        </p>
      </div>

      {/* Category Navigation */}
      <div className="flex flex-wrap justify-center gap-2">
        {Object.entries(components).map(([key, category]) => (
          <button
            key={key}
            onClick={() => setSelectedCategory(key)}
            className={`px-6 py-3  font-semibold transition-colors ${
              selectedCategory === key
                ? 'bg-[var(--mustard)] text-white'
                : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }`}
          >
            {category.title}
          </button>
        ))}
      </div>

      {/* Components Grid */}
      <div className="space-y-6">
        {currentCategory.items.map((component) => (
          <div key={component.id} className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-charcoal-900">{component.name}</h3>
                <p className="text-charcoal-600">{component.description}</p>
              </div>
              <button
                onClick={() => toggleExpanded(component.id)}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)] transition-colors"
              >
                {expandedComponents.has(component.id) ? (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Hide Code
                  </>
                ) : (
                  <>
                    <ChevronRight className="h-4 w-4" />
                    Show Code
                  </>
                )}
              </button>
            </div>

            {/* Preview */}
            <div className="mb-6 p-6 bg-[var(--surface)]">
              {component.preview}
            </div>

            {/* Code */}
            {expandedComponents.has(component.id) && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-charcoal-900">Code</h4>
                  <button className="flex items-center gap-2 px-3 py-1 bg-consulting-slate-blue-600 text-white rounded text-sm hover:bg-consulting-slate-blue-700 transition-colors">
                    <Copy className="h-3 w-3" />
                    Copy
                  </button>
                </div>
                <pre className="bg-charcoal-900 text-[var(--text-pri)] p-4 overflow-x-auto text-sm">
                  <code>{component.code}</code>
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Usage Guidelines */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Implementation Guidelines</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold text-charcoal-900 mb-3">Best Practices</h3>
            <ul className="space-y-2 text-sm text-charcoal-700">
              <li>• Maintain consistent spacing using Tailwind classes</li>
              <li>• Use semantic color names (primary, sage, amber, coral)</li>
              <li>• Ensure proper contrast ratios for accessibility</li>
              <li>• Test components across different screen sizes</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold text-charcoal-900 mb-3">Customization</h3>
            <ul className="space-y-2 text-sm text-charcoal-700">
              <li>• Modify colors using CSS custom properties</li>
              <li>• Adjust spacing with Tailwind spacing scale</li>
              <li>• Add animations with transition classes</li>
              <li>• Extend with additional utility classes as needed</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
